// =============================================================================
// i18n bootstrap — admin web.
//
// Languages supported (v1):  English, Romanian, Bulgarian, Polish, Hungarian, Latvian.
// Detection order:           localStorage('lang')  →  browser navigator  →  fallback 'en'.
// Namespaces (seeded):       common, nav, toast, validation.
//
// Adding a new string:
//   1. Put the English copy in src/i18n/locales/en/<namespace>.json
//   2. Mirror the same key in the other 5 language files (translate them yourself
//      — no external workflow is wired up yet).
//   3. In a component:
//        import { useTranslation } from "react-i18next";
//        const { t } = useTranslation();   // default ns is 'common'
//        // or: const { t } = useTranslation("nav");
//        <span>{t("save")}</span>
//
// Adding a new language:
//   1. Create src/i18n/locales/<code>/*.json
//   2. Add the code to SUPPORTED_LANGUAGES below + to the LanguageSwitcher options
//   3. Update resources{} with the import statements
// =============================================================================

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Namespaced JSON files — one block per language.
import enCommon     from "./locales/en/common.json";
import enNav        from "./locales/en/nav.json";
import enToast      from "./locales/en/toast.json";
import enValidation from "./locales/en/validation.json";
import enAuth       from "./locales/en/auth.json";
import enHeader     from "./locales/en/header.json";
import enHr         from "./locales/en/hr.json";
import enEmployee   from "./locales/en/employee.json";
import enHoliday    from "./locales/en/holiday.json";
import enClothing   from "./locales/en/clothing.json";
import enVolunteer  from "./locales/en/volunteer.json";
import enAttendance from "./locales/en/attendance.json";
import enFarms      from "./locales/en/farms.json";
import enTeams      from "./locales/en/teams.json";
import enVehicles   from "./locales/en/vehicles.json";
import enMachines   from "./locales/en/machines.json";
import enVanPhoto   from "./locales/en/vanPhoto.json";
import enTracking   from "./locales/en/tracking.json";
import enSanitization from "./locales/en/sanitization.json";
import enCustomers  from "./locales/en/customers.json";
import enCheckInOut from "./locales/en/checkInOut.json";
import enTraining   from "./locales/en/training.json";
import enCertifications from "./locales/en/certifications.json";
import enImportantDocs  from "./locales/en/importantDocs.json";
import enAuditDocRequest from "./locales/en/auditDocRequest.json";
import enAuditorTimesheet from "./locales/en/auditorTimesheet.json";
import enUserManagement from "./locales/en/userManagement.json";
import enNotification from "./locales/en/notification.json";
import enProfile      from "./locales/en/profile.json";
import enAudit       from "./locales/en/audit.json";
import enNcrAudit    from "./locales/en/ncrAudit.json";
import enAuditReport from "./locales/en/auditReport.json";
import enPages       from "./locales/en/pages.json";
import enReports     from "./locales/en/reports.json";
import enKanban      from "./locales/en/kanban.json";
import enShift       from "./locales/en/shift.json";
import enMachineAssignments from "./locales/en/machineAssignments.json";

import roCommon     from "./locales/ro/common.json";
import roNav        from "./locales/ro/nav.json";
import roToast      from "./locales/ro/toast.json";
import roValidation from "./locales/ro/validation.json";
import roAuth       from "./locales/ro/auth.json";
import roHeader     from "./locales/ro/header.json";
import roHr         from "./locales/ro/hr.json";
import roEmployee   from "./locales/ro/employee.json";
import roHoliday    from "./locales/ro/holiday.json";
import roClothing   from "./locales/ro/clothing.json";
import roVolunteer  from "./locales/ro/volunteer.json";
import roAttendance from "./locales/ro/attendance.json";
import roFarms      from "./locales/ro/farms.json";
import roTeams      from "./locales/ro/teams.json";
import roVehicles   from "./locales/ro/vehicles.json";
import roMachines   from "./locales/ro/machines.json";
import roVanPhoto   from "./locales/ro/vanPhoto.json";
import roTracking   from "./locales/ro/tracking.json";
import roSanitization from "./locales/ro/sanitization.json";
import roCustomers  from "./locales/ro/customers.json";
import roCheckInOut from "./locales/ro/checkInOut.json";
import roTraining   from "./locales/ro/training.json";
import roCertifications from "./locales/ro/certifications.json";
import roImportantDocs  from "./locales/ro/importantDocs.json";
import roAuditDocRequest from "./locales/ro/auditDocRequest.json";
import roAuditorTimesheet from "./locales/ro/auditorTimesheet.json";
import roUserManagement from "./locales/ro/userManagement.json";
import roNotification from "./locales/ro/notification.json";
import roProfile      from "./locales/ro/profile.json";
import roAudit       from "./locales/ro/audit.json";
import roNcrAudit    from "./locales/ro/ncrAudit.json";
import roAuditReport from "./locales/ro/auditReport.json";
import roPages       from "./locales/ro/pages.json";
import roReports     from "./locales/ro/reports.json";
import roKanban      from "./locales/ro/kanban.json";
import roShift       from "./locales/ro/shift.json";

import bgCommon     from "./locales/bg/common.json";
import bgNav        from "./locales/bg/nav.json";
import bgToast      from "./locales/bg/toast.json";
import bgValidation from "./locales/bg/validation.json";
import bgAuth       from "./locales/bg/auth.json";
import bgHeader     from "./locales/bg/header.json";
import bgHr         from "./locales/bg/hr.json";
import bgEmployee   from "./locales/bg/employee.json";
import bgHoliday    from "./locales/bg/holiday.json";
import bgClothing   from "./locales/bg/clothing.json";
import bgVolunteer  from "./locales/bg/volunteer.json";
import bgAttendance from "./locales/bg/attendance.json";
import bgFarms      from "./locales/bg/farms.json";
import bgTeams      from "./locales/bg/teams.json";
import bgVehicles   from "./locales/bg/vehicles.json";
import bgMachines   from "./locales/bg/machines.json";
import bgVanPhoto   from "./locales/bg/vanPhoto.json";
import bgTracking   from "./locales/bg/tracking.json";
import bgSanitization from "./locales/bg/sanitization.json";
import bgCustomers  from "./locales/bg/customers.json";
import bgCheckInOut from "./locales/bg/checkInOut.json";
import bgTraining   from "./locales/bg/training.json";
import bgCertifications from "./locales/bg/certifications.json";
import bgImportantDocs  from "./locales/bg/importantDocs.json";
import bgAuditDocRequest from "./locales/bg/auditDocRequest.json";
import bgAuditorTimesheet from "./locales/bg/auditorTimesheet.json";
import bgUserManagement from "./locales/bg/userManagement.json";
import bgNotification from "./locales/bg/notification.json";
import bgProfile      from "./locales/bg/profile.json";
import bgAudit       from "./locales/bg/audit.json";
import bgNcrAudit    from "./locales/bg/ncrAudit.json";
import bgAuditReport from "./locales/bg/auditReport.json";
import bgPages       from "./locales/bg/pages.json";
import bgReports     from "./locales/bg/reports.json";
import bgKanban      from "./locales/bg/kanban.json";
import bgShift       from "./locales/bg/shift.json";

import plCommon     from "./locales/pl/common.json";
import plNav        from "./locales/pl/nav.json";
import plToast      from "./locales/pl/toast.json";
import plValidation from "./locales/pl/validation.json";
import plAuth       from "./locales/pl/auth.json";
import plHeader     from "./locales/pl/header.json";
import plHr         from "./locales/pl/hr.json";
import plEmployee   from "./locales/pl/employee.json";
import plHoliday    from "./locales/pl/holiday.json";
import plClothing   from "./locales/pl/clothing.json";
import plVolunteer  from "./locales/pl/volunteer.json";
import plAttendance from "./locales/pl/attendance.json";
import plFarms      from "./locales/pl/farms.json";
import plTeams      from "./locales/pl/teams.json";
import plVehicles   from "./locales/pl/vehicles.json";
import plMachines   from "./locales/pl/machines.json";
import plVanPhoto   from "./locales/pl/vanPhoto.json";
import plTracking   from "./locales/pl/tracking.json";
import plSanitization from "./locales/pl/sanitization.json";
import plCustomers  from "./locales/pl/customers.json";
import plCheckInOut from "./locales/pl/checkInOut.json";
import plTraining   from "./locales/pl/training.json";
import plCertifications from "./locales/pl/certifications.json";
import plImportantDocs  from "./locales/pl/importantDocs.json";
import plAuditDocRequest from "./locales/pl/auditDocRequest.json";
import plAuditorTimesheet from "./locales/pl/auditorTimesheet.json";
import plUserManagement from "./locales/pl/userManagement.json";
import plNotification from "./locales/pl/notification.json";
import plProfile      from "./locales/pl/profile.json";
import plAudit       from "./locales/pl/audit.json";
import plNcrAudit    from "./locales/pl/ncrAudit.json";
import plAuditReport from "./locales/pl/auditReport.json";
import plPages       from "./locales/pl/pages.json";
import plReports     from "./locales/pl/reports.json";
import plKanban      from "./locales/pl/kanban.json";
import plShift       from "./locales/pl/shift.json";

import huCommon     from "./locales/hu/common.json";
import huNav        from "./locales/hu/nav.json";
import huToast      from "./locales/hu/toast.json";
import huValidation from "./locales/hu/validation.json";
import huAuth       from "./locales/hu/auth.json";
import huHeader     from "./locales/hu/header.json";
import huHr         from "./locales/hu/hr.json";
import huEmployee   from "./locales/hu/employee.json";
import huHoliday    from "./locales/hu/holiday.json";
import huClothing   from "./locales/hu/clothing.json";
import huVolunteer  from "./locales/hu/volunteer.json";
import huAttendance from "./locales/hu/attendance.json";
import huFarms      from "./locales/hu/farms.json";
import huTeams      from "./locales/hu/teams.json";
import huVehicles   from "./locales/hu/vehicles.json";
import huMachines   from "./locales/hu/machines.json";
import huVanPhoto   from "./locales/hu/vanPhoto.json";
import huTracking   from "./locales/hu/tracking.json";
import huSanitization from "./locales/hu/sanitization.json";
import huCustomers  from "./locales/hu/customers.json";
import huCheckInOut from "./locales/hu/checkInOut.json";
import huTraining   from "./locales/hu/training.json";
import huCertifications from "./locales/hu/certifications.json";
import huImportantDocs  from "./locales/hu/importantDocs.json";
import huAuditDocRequest from "./locales/hu/auditDocRequest.json";
import huAuditorTimesheet from "./locales/hu/auditorTimesheet.json";
import huUserManagement from "./locales/hu/userManagement.json";
import huNotification from "./locales/hu/notification.json";
import huProfile      from "./locales/hu/profile.json";
import huAudit       from "./locales/hu/audit.json";
import huNcrAudit    from "./locales/hu/ncrAudit.json";
import huAuditReport from "./locales/hu/auditReport.json";
import huPages       from "./locales/hu/pages.json";
import huReports     from "./locales/hu/reports.json";
import huKanban      from "./locales/hu/kanban.json";
import huShift       from "./locales/hu/shift.json";

import lvCommon     from "./locales/lv/common.json";
import lvNav        from "./locales/lv/nav.json";
import lvToast      from "./locales/lv/toast.json";
import lvValidation from "./locales/lv/validation.json";
import lvAuth       from "./locales/lv/auth.json";
import lvHeader     from "./locales/lv/header.json";
import lvHr         from "./locales/lv/hr.json";
import lvEmployee   from "./locales/lv/employee.json";
import lvHoliday    from "./locales/lv/holiday.json";
import lvClothing   from "./locales/lv/clothing.json";
import lvVolunteer  from "./locales/lv/volunteer.json";
import lvAttendance from "./locales/lv/attendance.json";
import lvFarms      from "./locales/lv/farms.json";
import lvTeams      from "./locales/lv/teams.json";
import lvVehicles   from "./locales/lv/vehicles.json";
import lvMachines   from "./locales/lv/machines.json";
import lvVanPhoto   from "./locales/lv/vanPhoto.json";
import lvTracking   from "./locales/lv/tracking.json";
import lvSanitization from "./locales/lv/sanitization.json";
import lvCustomers  from "./locales/lv/customers.json";
import lvCheckInOut from "./locales/lv/checkInOut.json";
import lvTraining   from "./locales/lv/training.json";
import lvCertifications from "./locales/lv/certifications.json";
import lvImportantDocs  from "./locales/lv/importantDocs.json";
import lvAuditDocRequest from "./locales/lv/auditDocRequest.json";
import lvAuditorTimesheet from "./locales/lv/auditorTimesheet.json";
import lvUserManagement from "./locales/lv/userManagement.json";
import lvNotification from "./locales/lv/notification.json";
import lvProfile      from "./locales/lv/profile.json";
import lvAudit       from "./locales/lv/audit.json";
import lvNcrAudit    from "./locales/lv/ncrAudit.json";
import lvAuditReport from "./locales/lv/auditReport.json";
import lvPages       from "./locales/lv/pages.json";
import lvReports     from "./locales/lv/reports.json";
import lvKanban      from "./locales/lv/kanban.json";
import lvShift       from "./locales/lv/shift.json";

import ruCommon     from "./locales/ru/common.json";
import ruNav        from "./locales/ru/nav.json";
import ruToast      from "./locales/ru/toast.json";
import ruValidation from "./locales/ru/validation.json";
import ruAuth       from "./locales/ru/auth.json";
import ruHeader     from "./locales/ru/header.json";
import ruHr         from "./locales/ru/hr.json";
import ruEmployee   from "./locales/ru/employee.json";
import ruHoliday    from "./locales/ru/holiday.json";
import ruClothing   from "./locales/ru/clothing.json";
import ruVolunteer  from "./locales/ru/volunteer.json";
import ruAttendance from "./locales/ru/attendance.json";
import ruFarms      from "./locales/ru/farms.json";
import ruTeams      from "./locales/ru/teams.json";
import ruVehicles   from "./locales/ru/vehicles.json";
import ruMachines   from "./locales/ru/machines.json";
import ruVanPhoto   from "./locales/ru/vanPhoto.json";
import ruTracking   from "./locales/ru/tracking.json";
import ruSanitization from "./locales/ru/sanitization.json";
import ruCustomers  from "./locales/ru/customers.json";
import ruCheckInOut from "./locales/ru/checkInOut.json";
import ruTraining   from "./locales/ru/training.json";
import ruCertifications from "./locales/ru/certifications.json";
import ruImportantDocs  from "./locales/ru/importantDocs.json";
import ruAuditDocRequest from "./locales/ru/auditDocRequest.json";
import ruAuditorTimesheet from "./locales/ru/auditorTimesheet.json";
import ruUserManagement from "./locales/ru/userManagement.json";
import ruNotification from "./locales/ru/notification.json";
import ruProfile      from "./locales/ru/profile.json";
import ruAudit       from "./locales/ru/audit.json";
import ruNcrAudit    from "./locales/ru/ncrAudit.json";
import ruAuditReport from "./locales/ru/auditReport.json";
import ruPages       from "./locales/ru/pages.json";
import ruReports     from "./locales/ru/reports.json";
import ruKanban      from "./locales/ru/kanban.json";
import ruShift       from "./locales/ru/shift.json";

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English",   flag: "🇬🇧", flagCode: "gb" },
  { code: "ro", label: "Română",    flag: "🇷🇴", flagCode: "ro" },
  { code: "bg", label: "Български", flag: "🇧🇬", flagCode: "bg" },
  { code: "pl", label: "Polski",    flag: "🇵🇱", flagCode: "pl" },
  { code: "hu", label: "Magyar",    flag: "🇭🇺", flagCode: "hu" },
  { code: "lv", label: "Latviešu",  flag: "🇱🇻", flagCode: "lv" },
  { code: "ru", label: "Русский",   flag: "🇷🇺", flagCode: "ru" },
] as const;

export type SupportedLanguageCode = typeof SUPPORTED_LANGUAGES[number]["code"];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: enCommon, nav: enNav, toast: enToast, validation: enValidation, auth: enAuth, header: enHeader, hr: enHr, employee: enEmployee, holiday: enHoliday, clothing: enClothing, volunteer: enVolunteer, attendance: enAttendance, farms: enFarms, teams: enTeams, vehicles: enVehicles, machines: enMachines, vanPhoto: enVanPhoto, tracking: enTracking, sanitization: enSanitization, customers: enCustomers, checkInOut: enCheckInOut, training: enTraining, certifications: enCertifications, importantDocs: enImportantDocs, auditDocRequest: enAuditDocRequest, auditorTimesheet: enAuditorTimesheet, userManagement: enUserManagement, notification: enNotification, profile: enProfile, audit: enAudit, ncrAudit: enNcrAudit, auditReport: enAuditReport, pages: enPages, reports: enReports, kanban: enKanban, shift: enShift, machineAssignments: enMachineAssignments },
      ro: { common: roCommon, nav: roNav, toast: roToast, validation: roValidation, auth: roAuth, header: roHeader, hr: roHr, employee: roEmployee, holiday: roHoliday, clothing: roClothing, volunteer: roVolunteer, attendance: roAttendance, farms: roFarms, teams: roTeams, vehicles: roVehicles, machines: roMachines, vanPhoto: roVanPhoto, tracking: roTracking, sanitization: roSanitization, customers: roCustomers, checkInOut: roCheckInOut, training: roTraining, certifications: roCertifications, importantDocs: roImportantDocs, auditDocRequest: roAuditDocRequest, auditorTimesheet: roAuditorTimesheet, userManagement: roUserManagement, notification: roNotification, profile: roProfile, audit: roAudit, ncrAudit: roNcrAudit, auditReport: roAuditReport, pages: roPages, reports: roReports, kanban: roKanban, shift: roShift },
      bg: { common: bgCommon, nav: bgNav, toast: bgToast, validation: bgValidation, auth: bgAuth, header: bgHeader, hr: bgHr, employee: bgEmployee, holiday: bgHoliday, clothing: bgClothing, volunteer: bgVolunteer, attendance: bgAttendance, farms: bgFarms, teams: bgTeams, vehicles: bgVehicles, machines: bgMachines, vanPhoto: bgVanPhoto, tracking: bgTracking, sanitization: bgSanitization, customers: bgCustomers, checkInOut: bgCheckInOut, training: bgTraining, certifications: bgCertifications, importantDocs: bgImportantDocs, auditDocRequest: bgAuditDocRequest, auditorTimesheet: bgAuditorTimesheet, userManagement: bgUserManagement, notification: bgNotification, profile: bgProfile, audit: bgAudit, ncrAudit: bgNcrAudit, auditReport: bgAuditReport, pages: bgPages, reports: bgReports, kanban: bgKanban, shift: bgShift },
      pl: { common: plCommon, nav: plNav, toast: plToast, validation: plValidation, auth: plAuth, header: plHeader, hr: plHr, employee: plEmployee, holiday: plHoliday, clothing: plClothing, volunteer: plVolunteer, attendance: plAttendance, farms: plFarms, teams: plTeams, vehicles: plVehicles, machines: plMachines, vanPhoto: plVanPhoto, tracking: plTracking, sanitization: plSanitization, customers: plCustomers, checkInOut: plCheckInOut, training: plTraining, certifications: plCertifications, importantDocs: plImportantDocs, auditDocRequest: plAuditDocRequest, auditorTimesheet: plAuditorTimesheet, userManagement: plUserManagement, notification: plNotification, profile: plProfile, audit: plAudit, ncrAudit: plNcrAudit, auditReport: plAuditReport, pages: plPages, reports: plReports, kanban: plKanban, shift: plShift },
      hu: { common: huCommon, nav: huNav, toast: huToast, validation: huValidation, auth: huAuth, header: huHeader, hr: huHr, employee: huEmployee, holiday: huHoliday, clothing: huClothing, volunteer: huVolunteer, attendance: huAttendance, farms: huFarms, teams: huTeams, vehicles: huVehicles, machines: huMachines, vanPhoto: huVanPhoto, tracking: huTracking, sanitization: huSanitization, customers: huCustomers, checkInOut: huCheckInOut, training: huTraining, certifications: huCertifications, importantDocs: huImportantDocs, auditDocRequest: huAuditDocRequest, auditorTimesheet: huAuditorTimesheet, userManagement: huUserManagement, notification: huNotification, profile: huProfile, audit: huAudit, ncrAudit: huNcrAudit, auditReport: huAuditReport, pages: huPages, reports: huReports, kanban: huKanban, shift: huShift },
      lv: { common: lvCommon, nav: lvNav, toast: lvToast, validation: lvValidation, auth: lvAuth, header: lvHeader, hr: lvHr, employee: lvEmployee, holiday: lvHoliday, clothing: lvClothing, volunteer: lvVolunteer, attendance: lvAttendance, farms: lvFarms, teams: lvTeams, vehicles: lvVehicles, machines: lvMachines, vanPhoto: lvVanPhoto, tracking: lvTracking, sanitization: lvSanitization, customers: lvCustomers, checkInOut: lvCheckInOut, training: lvTraining, certifications: lvCertifications, importantDocs: lvImportantDocs, auditDocRequest: lvAuditDocRequest, auditorTimesheet: lvAuditorTimesheet, userManagement: lvUserManagement, notification: lvNotification, profile: lvProfile, audit: lvAudit, ncrAudit: lvNcrAudit, auditReport: lvAuditReport, pages: lvPages, reports: lvReports, kanban: lvKanban, shift: lvShift },
      ru: { common: ruCommon, nav: ruNav, toast: ruToast, validation: ruValidation, auth: ruAuth, header: ruHeader, hr: ruHr, employee: ruEmployee, holiday: ruHoliday, clothing: ruClothing, volunteer: ruVolunteer, attendance: ruAttendance, farms: ruFarms, teams: ruTeams, vehicles: ruVehicles, machines: ruMachines, vanPhoto: ruVanPhoto, tracking: ruTracking, sanitization: ruSanitization, customers: ruCustomers, checkInOut: ruCheckInOut, training: ruTraining, certifications: ruCertifications, importantDocs: ruImportantDocs, auditDocRequest: ruAuditDocRequest, auditorTimesheet: ruAuditorTimesheet, userManagement: ruUserManagement, notification: ruNotification, profile: ruProfile, audit: ruAudit, ncrAudit: ruNcrAudit, auditReport: ruAuditReport, pages: ruPages, reports: ruReports, kanban: ruKanban, shift: ruShift },
    },

    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    nonExplicitSupportedLngs: true, // e.g. "en-GB" → "en"

    ns: ["common", "nav", "toast", "validation", "auth", "header", "hr", "employee", "holiday", "clothing", "volunteer", "attendance", "farms", "teams", "vehicles", "machines", "vanPhoto", "tracking", "sanitization", "customers", "checkInOut", "training", "certifications", "importantDocs", "auditDocRequest", "auditorTimesheet", "userManagement", "notification", "profile", "audit", "ncrAudit", "auditReport", "pages", "reports", "kanban", "shift", "machineAssignments"],
    defaultNS: "common",

    detection: {
      // Read preference in this order, then persist the active choice to localStorage.
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: "lang",
    },

    interpolation: {
      escapeValue: false, // React handles XSS escaping itself
    },

    returnNull: false,
  });

export default i18n;
